/**
 * JSON操作ユーティリティ
 *
 * オブジェクトのフラット化、値取得などの共通処理
 */

/**
 * オブジェクトをフラットなキーバリューペアにする
 * rawTextキーは除外
 */
export function flattenObject(
  obj: Record<string, unknown>,
  prefix = ""
): { key: string; value: string }[] {
  const result: { key: string; value: string }[] = [];

  for (const [key, value] of Object.entries(obj)) {
    // rawTextは除外
    if (key === "rawText") continue;

    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      // ネストされたオブジェクトは再帰的にフラット化
      result.push(
        ...flattenObject(value as Record<string, unknown>, fullKey)
      );
    } else if (Array.isArray(value)) {
      // 配列は各要素を展開
      value.forEach((item, index) => {
        if (item && typeof item === "object") {
          result.push(
            ...flattenObject(
              item as Record<string, unknown>,
              `${fullKey}[${index}]`
            )
          );
        } else {
          result.push({ key: `${fullKey}[${index}]`, value: String(item ?? "") });
        }
      });
    } else {
      result.push({ key: fullKey, value: String(value ?? "") });
    }
  }

  return result;
}

/**
 * JSONからフラットなパスのリストを作成
 * CSV設定のパス選択用
 */
export function getFlattenPaths(
  obj: Record<string, unknown>,
  prefix = ""
): { path: string; value: unknown }[] {
  let paths: { path: string; value: unknown }[] = [];

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    // rawTextは表示から除外
    if (key === "rawText") continue;

    const path = prefix ? `${prefix}.${key}` : key;
    const val = obj[key];

    if (Array.isArray(val)) {
      // 配列（明細）の場合は[0]を使用してパスを生成
      if (val.length > 0) {
        const arrayPath = `${path}[]`;
        paths.push({ path: arrayPath, value: "(明細データ)" });
        const firstItem = val[0];
        if (typeof firstItem === "object" && firstItem !== null) {
          paths = paths.concat(
            getFlattenPaths(firstItem as Record<string, unknown>, arrayPath)
          );
        }
      }
    } else if (typeof val === "object" && val !== null) {
      paths = paths.concat(getFlattenPaths(val as Record<string, unknown>, path));
    } else {
      paths.push({ path, value: val });
    }
  }

  return paths;
}

/**
 * dot.notation や array[].prop に対応して値を取得する
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getJsonValue(obj: any, path: string): string | undefined {
  if (!obj || !path) return undefined;

  // 1. まずパスそのものでアクセスを試みる
  if (obj[path] !== undefined) {
    if (typeof obj[path] === "object") return JSON.stringify(obj[path]);
    return String(obj[path]);
  }

  // 2. ドットで分割して構造を探索
  const parts = path.split(".");
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;

    // 配列記法 "key[]" または "key[0]" の処理
    const arrayMatch = part.match(/^([^[]+)\[(\d*)\]$/);
    if (arrayMatch) {
      const key = arrayMatch[1];
      const index = arrayMatch[2] ? parseInt(arrayMatch[2], 10) : 0;

      if (Array.isArray(current[key])) {
        current = current[key][index];
      } else if (current[part] !== undefined) {
        current = current[part];
      } else {
        return undefined;
      }
    } else {
      if (current[part] !== undefined) {
        current = current[part];
      } else if (Array.isArray(current) && !isNaN(Number(part))) {
        current = current[Number(part)];
      } else {
        return undefined;
      }
    }
  }

  if (current === undefined || current === null) return undefined;
  if (typeof current === "object") return JSON.stringify(current);
  return String(current);
}
